import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  AppBar
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PolicyIcon from '@material-ui/icons/Policy'
import { Form, Formik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumbs from '../../components/Breadcrumb'
import SectionTitle from '../../components/SectionTitle'
import {
  MODIFY_POLICY_GENERATE,
  MODIFY_POLICY_QUERY,
  MODIFY_POLICY_MODIFY
} from '../../graphql/modifyPolicy'
import { getTranslate, lang } from '../../localization'
import { CancelPolicy, PolicyTable } from '../../types/cancelAndModifyPolicies'
import { Multilingual } from '../../types/roomtype'
import PoliciesFormAddPoliciesModal from './PoliciesFormAddPoliciesModal'
import PoliciesFormAvailablePoliciesList from './PoliciesFormAvailablePoliciesList'
import PoliciesFormInputBox from './PoliciesFormInputBox'
import { sortPolicies } from './policiesSorter'
import useStyles from './styles/PoliciesFormMain.styles'
import { CancelPolicyFormValidationSchema } from './validations'

const CANCELATION_POLICY_FORM_INIT_VALUES: CancelPolicy = {
  basicDetails: {
    name: '',
    description: '',
    descriptionTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    id: ''
  },
  table: []
}

const ModifyPoliciesForm = () => {
  const history = useHistory()
  const { providerCode, id } = useParams<{
    providerCode: string
    id: string
  }>()

  const [cancelPolicyOnSubmitFunction, { loading: loadingForModify }] =
    useMutation(id ? MODIFY_POLICY_MODIFY : MODIFY_POLICY_GENERATE, {
      notifyOnNetworkStatusChange: true
    })

  const [descriptionMultiLingual, setdescriptionMultiLingual] = useState<
    Multilingual[]
  >([])

  const [getInitialValues, { loading: loadingForData }] = useLazyQuery<{
    modifyPolicy: CancelPolicy
  }>(MODIFY_POLICY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })
  const [initialValues, setinitialValues] = useState<CancelPolicy>(
    CANCELATION_POLICY_FORM_INIT_VALUES
  )
  const [providerCodeState, setproviderCodeState] =
    useState<string>(providerCode)

  const getInitialValuesFunction = async () => {
    const { data } = await getInitialValues({
      variables: { providerCode: providerCodeState, policyID: id }
    })

    const initValues = data?.modifyPolicy!

    setinitialValues(initValues)

    if (initValues.basicDetails.descriptionTranslatedList.length > 0) {
      setdescriptionMultiLingual(
        initValues.basicDetails.descriptionTranslatedList
      )
    }
  }

  const handleSubmit = async (values: CancelPolicy) => {
    const tempTable: PolicyTable[] = []
    values.table.forEach((t) => {
      const singleTableRow: PolicyTable = {
        deadline: t.deadline,
        deadlineTimeOffset: t.deadlineTimeOffset,
        penaltyAmount: t.penaltyAmount,
        penaltyType: t.penaltyType,
        policyType: t.policyType,
        priority: t.priority
      }

      tempTable.push(singleTableRow)
    })

    try {
      const { data: response } = await cancelPolicyOnSubmitFunction({
        variables: {
          input: {
            table: tempTable,
            basicDetails: {
              ...values.basicDetails,
              descriptionTranslatedList: descriptionMultiLingual
            }
          },
          providerCode: providerCode ? providerCode : providerCodeState
        }
      })
      if (id) {
        toast.success(response.modifyModifyPolicy.message)
        history.push(`/${lang}/policies/amendment-policies`)
      } else {
        toast.success(response.generateModifyPolicy.message)
        history.push(`/${lang}/policies/amendment-policies`)
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }
  const classes = useStyles()
  const [policiesModalIsOpen, setpoliciesModalIsOpen] = useState<boolean>(false)
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (id) {
      getInitialValuesFunction()
    }
  }, [])

  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Amendment policies'),
        link: '/policies/amendment-policies'
      },
      {
        label: initialValues.basicDetails.name
      }
    ],
    [initialValues]
  )

  return (
    <div className={classes.root}>
      {id && (
        <AppBar
          className={classes.appBar}
          position="static"
          color="transparent">
          <Breadcrumbs levels={cachedLevels} loading={loadingForData} />
        </AppBar>
      )}
      <div className={classes.contentContainer}>
        <div className={classes.header}>
          <PolicyIcon fontSize="large" />
          <Typography className={classes.title} variant="h3" gutterBottom>
            {getTranslate(
              id ? initialValues.basicDetails.name : 'Add New Policies'
            )}
          </Typography>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={CancelPolicyFormValidationSchema}>
          {({
            values,
            setFieldValue,
            handleBlur,
            isSubmitting,
            errors,
            touched,
            isValid
          }) => {
            return (
              <Form>
                <div>
                  <div className={classes.titleContainer}>
                    <SectionTitle title="Basic Details" marginBottom />
                    {loadingForData ? <CircularProgress /> : null}{' '}
                  </div>
                  <PoliciesFormInputBox
                    {...{
                      values,
                      setFieldValue,
                      handleBlur,
                      isSubmitting,
                      errors,
                      touched,
                      classes,
                      loadingForData,
                      providerCodeState,
                      setproviderCodeState
                    }}
                    descriptionMultiLingual={descriptionMultiLingual}
                    setdescriptionMultiLingual={setdescriptionMultiLingual}
                  />
                  <div className={classes.tableHeaderContainer}>
                    <SectionTitle title="Policies" marginBottom />
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      size="medium"
                      fullWidth={matches}
                      startIcon={<AddIcon />}
                      onClick={() => setpoliciesModalIsOpen(true)}>
                      {getTranslate('Add Policies')}
                    </Button>
                  </div>
                  <PoliciesFormAddPoliciesModal
                    {...{
                      values,
                      setFieldValue,
                      classes
                    }}
                    isOpen={policiesModalIsOpen}
                    onClose={() => setpoliciesModalIsOpen(false)}
                  />
                  <div>
                    {sortPolicies(values.table) ? (
                      <PoliciesFormAvailablePoliciesList
                        onDelete={(data: PolicyTable) => {
                          var temp = [...values.table].filter(
                            (item) => item !== data
                          )
                          setFieldValue('table', temp)
                        }}
                        policiesData={sortPolicies(values.table)}
                      />
                    ) : (
                      <Typography variant="h4" className={classes.noPilocies}>
                        {getTranslate('No Policies')}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <div className={classes.footer}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isValid || loadingForModify}
                        size="medium"
                        fullWidth={matches}>
                        {getTranslate('Save Policies')}
                        {loadingForModify && (
                          <CircularProgress
                            className={classes.circularProgress}
                            size={16}
                          />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}
export default ModifyPoliciesForm
